import React, { useState } from "react";
import { Row, Col, Form, Button, Badge, Spinner, Alert } from "react-bootstrap";
import api from "../../api";
import { FaSearch, FaRedo } from "react-icons/fa";
import { ShieldCheck } from "lucide-react";

const STEP = { FORM: "form", VERIFIED: "verified" };

// ── Step progress bar ─────────────────────────────────────
const StepBar = ({ step }) => {
  const steps = [
    { id: STEP.FORM,     label: "Verify"  },
    { id: STEP.VERIFIED, label: "Confirm" },
  ];
  const cur = steps.findIndex((s) => s.id === step);

  return (
    <div className="otp-step-bar">
      {steps.map((s, i) => {
        const done   = i < cur;
        const active = i === cur;
        return (
          <React.Fragment key={s.id}>
            <div className="otp-step-item">
              <div className={`otp-step-circle ${done ? "otp-step-circle--done" : active ? "otp-step-circle--active" : "otp-step-circle--todo"}`}>
                {done ? "✓" : i + 1}
              </div>
              <span className={`otp-step-label ${done ? "otp-step-label--done" : active ? "otp-step-label--active" : ""}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`otp-step-connector ${done ? "otp-step-connector--done" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Main component ────────────────────────────────────────
const OtpLookup = () => {
  const [customerCode, setCustomerCode] = useState("");
  const [branchCode,   setBranchCode]   = useState("");
  const [step,         setStep]         = useState(STEP.FORM);
  const [customer,     setCustomer]     = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");

  const reset = () => {
    setCustomerCode(""); setBranchCode(""); setStep(STEP.FORM);
    setCustomer(null);   setError("");
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.get("/api/otp-lookup/customer", {
        params: { customerCode: customerCode.trim(), branchCode: branchCode.trim() },
      });
      setCustomer(data);
      setStep(STEP.VERIFIED);
    } catch (err) {
      setError(err.response?.data?.error || "Customer not found. Check the code and branch.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Step progress */}
      <StepBar step={step} />

      {/* Body */}
      <div className="csd-otp-body">
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-3" style={{ fontSize: "0.85rem", borderRadius: 9 }}>
            {error}
          </Alert>
        )}

        {/* ── Step 1: Input form ────────────────────── */}
        {step === STEP.FORM && (
          <div>
            <p className="fw-semibold mb-1" style={{ fontSize: "0.95rem", color: "#0f172a" }}>Verify Customer Identity</p>
            <p className="text-muted mb-4" style={{ fontSize: "0.8rem" }}>Enter the customer code and branch code to look up their record.</p>

            <Form onSubmit={handleVerify}>
              <Row className="g-3 mb-4">
                <Col sm={6}>
                  <Form.Group className="otp-form-group">
                    <Form.Label>Customer Code</Form.Label>
                    <Form.Control
                      placeholder="e.g. R3980"
                      value={customerCode}
                      onChange={(e) => setCustomerCode(e.target.value)}
                      required
                      autoFocus
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group className="otp-form-group">
                    <Form.Label>Branch Code</Form.Label>
                    <Form.Control
                      placeholder="e.g. 001"
                      value={branchCode}
                      onChange={(e) => setBranchCode(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="w-100 fw-semibold"
                style={{ borderRadius: 9, padding: "10px", fontSize: "0.9rem" }}
              >
                {loading
                  ? <><Spinner size="sm" className="me-2" />Verifying…</>
                  : <><FaSearch className="me-2" />Verify Customer</>}
              </Button>
            </Form>
          </div>
        )}

        {/* ── Step 2: Customer status ───────────────── */}
        {step === STEP.VERIFIED && customer && (
          <div>
            <div className="otp-verified-banner">
              <ShieldCheck size={16} />
              Customer Identity Verified
            </div>

            <div className="otp-customer-card mb-3">
              <Row className="g-2">
                <Col xs={12}>
                  <p className="otp-customer-field-label">Customer Name</p>
                  <p className="otp-customer-field-value mb-0">{customer.customerName || "—"}</p>
                </Col>
                <Col xs={6}>
                  <p className="otp-customer-field-label">Status</p>
                  <Badge
                    bg={customer.customerStatus === "Active" ? "success" : "secondary"}
                    className="fw-normal"
                    style={{ fontSize: "0.72rem", borderRadius: 6 }}
                  >
                    {customer.customerStatus}
                  </Badge>
                </Col>
                <Col xs={6}>
                  <p className="otp-customer-field-label">Branch Code</p>
                  <p className="otp-customer-field-value mb-0">{customer.branchCode}</p>
                </Col>
                <Col xs={6}>
                  <p className="otp-customer-field-label">Mobile</p>
                  <p className="otp-customer-field-value mb-0 font-monospace">{customer.mobile || "—"}</p>
                </Col>
                {customer.address && (
                  <Col xs={12}>
                    <p className="otp-customer-field-label">Address</p>
                    <p className="otp-customer-field-value mb-0" style={{ fontSize: "0.82rem" }}>{customer.address}</p>
                  </Col>
                )}
              </Row>
            </div>

            <Button variant="outline-secondary" onClick={reset} className="w-100" style={{ borderRadius: 9, fontSize: "0.85rem" }}>
              <FaRedo size={12} className="me-1" /> New Lookup
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OtpLookup;
